import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { API_ENDPOINTS } from '../config/api'
import { assignmentsAPI } from '../api/assignments'

interface ComplianceMember {
  member_id: string
  email: string
  name: string
  status: string
  attempts: number
  last_taken: string | null
  next_due: string | null
  reminders_sent: number
  baseline_score: number | null
  latest_score: number | null
  delta: number | null
}

interface HistoryAttempt {
  id: string
  attempt_number: number
  submitted_at: string
  score: number | null
  overall_percentage: number | null
  group_results: any
  responses: Record<string, any>
}

const STATUS_COLORS: Record<string, string> = {
  COMPLETED: 'bg-green-100 text-green-800',
  DUE: 'bg-yellow-100 text-yellow-800',
  OVERDUE: 'bg-red-100 text-red-800',
  INCOMPLETE: 'bg-orange-100 text-orange-800',
  PENDING: 'bg-blue-100 text-blue-800',
  WAIVED: 'bg-gray-100 text-gray-600',
  NOT_ASSIGNED: 'bg-gray-50 text-gray-400',
}

const FILTER_OPTIONS = ['All', 'Due', 'Overdue', 'Incomplete', 'Completed', 'Pending', 'Waived']

const CompliancePage: React.FC = () => {
  const intl = useIntl()
  const { surveyId = '', orgId = '' } = useParams<{ surveyId: string; orgId: string }>()
  const [data, setData] = useState<ComplianceMember[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('All')
  const [search, setSearch] = useState('')
  const [historyEmail, setHistoryEmail] = useState<string | null>(null)
  const [history, setHistory] = useState<HistoryAttempt[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [surveyTitle, setSurveyTitle] = useState('')

  const token = localStorage.getItem('access_token')
  const headers: Record<string, string> = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  }

  useEffect(() => {
    fetchCompliance()
    fetchSurveyTitle()
  }, [surveyId])

  const fetchSurveyTitle = async () => {
    try {
      const res = await fetch(API_ENDPOINTS.surveys.byId(surveyId), { headers })
      if (res.ok) {
        const s = await res.json()
        setSurveyTitle(s.title || '')
      }
    } catch (err) {
      console.error('Failed to fetch survey title:', err)
    }
  }

  const fetchCompliance = async () => {
    try {
      setLoading(true)
      const res = await fetch(API_ENDPOINTS.surveys.compliance(surveyId), { headers })
      if (!res.ok) throw new Error(intl.formatMessage({ id: 'compliance.loadError' }))
      setData(await res.json())
    } catch (err: any) {
      setError(err.message || intl.formatMessage({ id: 'compliance.loadError' }))
    } finally {
      setLoading(false)
    }
  }

  const fetchHistory = async (email: string) => {
    setHistoryEmail(email)
    setHistoryLoading(true)
    try {
      const res = await fetch(API_ENDPOINTS.surveys.respondentHistory(surveyId, email), { headers })
      if (res.ok) setHistory(await res.json())
    } catch (err) {
      console.error('Failed to fetch history:', err)
    } finally {
      setHistoryLoading(false)
    }
  }

  const handleExtend = async (m: ComplianceMember) => {
    const days = prompt(intl.formatMessage({ id: 'compliance.extendPrompt' }), '7')
    if (!days) return
    try {
      await assignmentsAPI.extendDueDate(orgId, surveyId, m.member_id, parseInt(days))
      await fetchCompliance()
    } catch (err) {
      console.error('Extend failed:', err)
    }
  }

  const handleWaive = async (m: ComplianceMember) => {
    if (!confirm(intl.formatMessage({ id: 'compliance.waiveConfirm' }, { name: m.name || m.email }))) return
    try {
      await assignmentsAPI.waiveAssignment(orgId, surveyId, m.member_id)
      await fetchCompliance()
    } catch (err) {
      console.error('Waive failed:', err)
    }
  }

  const handleRemind = async (m: ComplianceMember) => {
    try {
      await assignmentsAPI.sendReminder(orgId, surveyId, m.member_id)
      alert(intl.formatMessage({ id: 'compliance.reminderSent' }, { email: m.email }))
      await fetchCompliance()
    } catch (err) {
      console.error('Remind failed:', err)
    }
  }

  const filtered = data.filter(m => {
    if (filter !== 'All' && m.status !== filter.toUpperCase()) return false
    if (search) {
      const q = search.toLowerCase()
      return m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q)
    }
    return true
  })

  const formatDate = (iso: string | null) => {
    if (!iso) return '-'
    return new Date(iso).toLocaleDateString()
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-foreground-muted"><FormattedMessage id="compliance.loading" /></p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-red-500">{error}</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-surface shadow-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <Link to="/dashboard" className="text-sm text-primary hover:underline">&larr; <FormattedMessage id="compliance.dashboard" /></Link>
            <h1 className="text-xl font-bold text-foreground mt-1">
              <FormattedMessage id="compliance.header" /> {surveyTitle && <span className="text-foreground-muted font-normal">- {surveyTitle}</span>}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-foreground-muted"><FormattedMessage id="compliance.memberCount" values={{ count: data.length }} /></span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="flex gap-1">
            {FILTER_OPTIONS.map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  filter === f
                    ? 'bg-primary text-white'
                    : 'bg-surface border border-border text-foreground hover:bg-background'
                }`}
              >
                {intl.formatMessage({ id: `compliance.filter.${f}` })}
                {f !== 'All' && (
                  <span className="ml-1 text-xs opacity-70">
                    ({data.filter(m => m.status === f.toUpperCase()).length})
                  </span>
                )}
              </button>
            ))}
          </div>
          <input
            type="text"
            placeholder={intl.formatMessage({ id: 'compliance.searchPlaceholder' })}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-lg border border-border bg-surface text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* Table */}
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-background">
                  <th className="text-left px-4 py-3 font-semibold text-foreground"><FormattedMessage id="compliance.col.member" /></th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground"><FormattedMessage id="compliance.col.status" /></th>
                  <th className="text-center px-4 py-3 font-semibold text-foreground"><FormattedMessage id="compliance.col.attempts" /></th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground"><FormattedMessage id="compliance.col.lastTaken" /></th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground"><FormattedMessage id="compliance.col.nextDue" /></th>
                  <th className="text-center px-4 py-3 font-semibold text-foreground"><FormattedMessage id="compliance.col.reminders" /></th>
                  <th className="text-center px-4 py-3 font-semibold text-foreground"><FormattedMessage id="compliance.col.baseline" /></th>
                  <th className="text-center px-4 py-3 font-semibold text-foreground"><FormattedMessage id="compliance.col.latest" /></th>
                  <th className="text-center px-4 py-3 font-semibold text-foreground"><FormattedMessage id="compliance.col.delta" /></th>
                  <th className="text-right px-4 py-3 font-semibold text-foreground"><FormattedMessage id="compliance.col.actions" /></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(m => (
                  <tr key={m.member_id} className="border-b border-border-light hover:bg-background/50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{m.name || intl.formatMessage({ id: 'compliance.unknown' })}</div>
                      <div className="text-xs text-foreground-muted">{m.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[m.status] || 'bg-gray-100 text-gray-600'}`}>
                        {intl.formatMessage({ id: `compliance.status.${m.status}`, defaultMessage: m.status })}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">{m.attempts}</td>
                    <td className="px-4 py-3 text-foreground-muted">{formatDate(m.last_taken)}</td>
                    <td className="px-4 py-3 text-foreground-muted">{formatDate(m.next_due)}</td>
                    <td className="px-4 py-3 text-center">{m.reminders_sent}/3</td>
                    <td className="px-4 py-3 text-center">
                      {m.baseline_score != null ? m.baseline_score.toFixed(1) : '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {m.latest_score != null ? m.latest_score.toFixed(1) : '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {m.delta != null ? (
                        <span className={m.delta > 0 ? 'text-green-600' : m.delta < 0 ? 'text-red-600' : 'text-foreground-muted'}>
                          {m.delta > 0 ? '+' : ''}{m.delta.toFixed(1)}
                          {m.delta > 0 ? ' \u2191' : m.delta < 0 ? ' \u2193' : ''}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex gap-1 justify-end">
                        {['DUE', 'OVERDUE', 'PENDING'].includes(m.status) && (
                          <>
                            <button onClick={() => handleExtend(m)} className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100"><FormattedMessage id="compliance.action.extend" /></button>
                            <button onClick={() => handleWaive(m)} className="px-2 py-1 text-xs bg-gray-50 text-gray-700 rounded hover:bg-gray-100"><FormattedMessage id="compliance.action.waive" /></button>
                            <button onClick={() => handleRemind(m)} className="px-2 py-1 text-xs bg-primary/10 text-primary rounded hover:bg-primary/20"><FormattedMessage id="compliance.action.remind" /></button>
                          </>
                        )}
                        {m.attempts > 0 && (
                          <button onClick={() => fetchHistory(m.email)} className="px-2 py-1 text-xs bg-purple-50 text-purple-700 rounded hover:bg-purple-100"><FormattedMessage id="compliance.action.history" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-4 py-8 text-center text-foreground-muted">
                      <FormattedMessage id="compliance.noMembers" />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* History Modal */}
      {historyEmail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setHistoryEmail(null)}>
          <div className="bg-surface rounded-xl shadow-lg max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-border flex justify-between items-center">
              <div>
                <h2 className="text-lg font-bold text-foreground"><FormattedMessage id="compliance.responseHistory" /></h2>
                <p className="text-sm text-foreground-muted">{historyEmail}</p>
              </div>
              <button onClick={() => setHistoryEmail(null)} className="text-foreground-muted hover:text-foreground text-xl">&times;</button>
            </div>
            <div className="p-6">
              {historyLoading ? (
                <p className="text-foreground-muted"><FormattedMessage id="compliance.modalLoading" /></p>
              ) : history.length === 0 ? (
                <p className="text-foreground-muted"><FormattedMessage id="compliance.noAttempts" /></p>
              ) : (
                <div className="space-y-3">
                  {history.map(h => (
                    <div key={h.id} className="flex items-center justify-between p-3 rounded-lg bg-background border border-border-light">
                      <div>
                        <span className="font-medium text-foreground"><FormattedMessage id="compliance.attemptNumber" values={{ number: h.attempt_number }} /></span>
                        <span className="text-sm text-foreground-muted ml-3">
                          {new Date(h.submitted_at).toLocaleDateString()} {new Date(h.submitted_at).toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="text-right">
                        {h.score != null && <span className="font-bold text-foreground"><FormattedMessage id="compliance.score" values={{ score: h.score.toFixed(1) }} /></span>}
                        {h.overall_percentage != null && (
                          <span className="text-sm text-foreground-muted ml-2">({h.overall_percentage.toFixed(0)}%)</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CompliancePage
